import {
  KYCPayload,
  PasswordData,
  ProfileEliteInterestData,
  ProfileImageData,
  SelfExclusionData,
  UserData,
} from "@/types";
import { api, backendApi } from "./api.service";
import { useMutation, useQuery } from "@tanstack/react-query";

export type PaginateNotificationResp = {
  data: Notification[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  currentPage: number;
};

export type Notification = {
  id: string;
  title: string;
  time: string;
  message: string;
  icon?: string;
  isNew: boolean;
};

export const useGetNotification = () => {
  return useMutation({
    mutationFn: ({ page, pageSize }: { page: number; pageSize: number }) => {
      const config = {
        params: {
          page,
          pageSize,
        },
      };
      return api.get(`/profile/notifications`, config);
    },
  });
};
export const useGetAccountData = () => {
  return useQuery({
    queryKey: ["get-account-data"],
    queryFn: async () => api.get("/profile/account"),
    enabled: false,
    retry: false,
  });
};

export const useGetUserData = () => {
  return useQuery({
    retry: 0,
    queryKey: ["get-user-data"],
    queryFn: async (data: any) => {
      const response = await api.get("/profile/settings", data);
      const body = await response.data;

      return body.data;
    },
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: (userData: UserData) => {
      return api.put("/profile/settings", userData);
    },
  });
};

export const useUpdateProfileImage = () => {
  return useMutation({
    mutationFn: (imageData: ProfileImageData) => {
      return api.put("/profile/settings/change-profile-picture", imageData);
    },
  });
};

export const useUpdateProfileEliteInterest = () => {
  return useMutation({
    mutationFn: (data: ProfileEliteInterestData) => {
      return api.put("/profile/elite-interest", data);
    },
  });
};

export const useChangeUserPassword = () => {
  return useMutation({
    mutationFn: (passwordData: PasswordData) => {
      return api.put("/profile/settings/change-password", passwordData);
    },
  });
};

export const useUpdateKYCVerification = () => {
  return useMutation({
    mutationFn: (data: KYCPayload) =>
      api.put("/profile/settings/kyc-verification", data),
  });
};

export const useGetKYCVerification = () => {
  return useMutation({
    mutationFn: () => api.get("/profile/settings/get-kyc-verify"),
  });
};

export const useGetSelfExclusion = () => {
  return useQuery({
    queryKey: ["get-self-exclusion"],
    queryFn: async () => {
      const res = await api.get("/profile/settings/self-exclusion");

      const body = await res.data.data;

      return body;
    },
    gcTime: 0,
  });
};

export const useRequestSelfExclusion = () => {
  return useMutation({
    mutationFn: (data: SelfExclusionData) =>
      api.post("/profile/settings/self-exclusion", data),
  });
};

export const useGetActiveSessions = () => {
  return useQuery({
    retry: 0,
    queryKey: ["get-active-sessions"],
    queryFn: async () => {
      const response = await api.get("/sessions");
      const body = await response.data;

      return body.data;
    },
  });
};

export const useRevokeSession = () => {
  return useMutation({
    mutationFn: (sessionid: string) => {
      return api.delete(`/sessions/${sessionid}`);
    },
  });
};

export const useGetGameSessions = () => {
  return useMutation({
    mutationFn: ({ page, pageSize }: { page: number; pageSize: number }) =>
      api.get("/gamesessions", {
        params: {
          page,
          pageSize,
        },
      }),
  });
};

export const useGetRakebackTransactions = (params: {
  id: string;
  page: number;
  pageSize: number;
  claimedOnly: boolean;
}) => {
  return useQuery({
    queryKey: ["get-rakebacks", params],
    queryFn: async () => {
      const response = await api.get(
        `/profile/settings/rakeback/user/${params.id}`,
        {
          params,
        },
      );

      const body = await response.data;
      return body;
    },
  });
};

export const useGetClaimedRakebackTransactions = () => {
  return useMutation({
    mutationFn: ({
      id,
      page,
      pageSize,
    }: {
      id: string;
      page: number;
      pageSize: number;
    }) => {
      return api.get(`/profile/settings/rakeback/user/${id}/all-claimed`, {
        params: {
          page,
          pageSize,
        },
      });
    },
  });
};

export const useGetIncomingRakebackTransactions = () => {
  return useMutation({
    mutationFn: ({
      id,
      page,
      pageSize,
    }: {
      id: string;
      page: number;
      pageSize: number;
    }) => {
      return api.get(`/profile/settings/rakeback/user/${id}/incoming`, {
        params: {
          page,
          pageSize,
        },
      });
    },
  });
};

export const useClaimAllRakeback = () => {
  return useMutation({
    mutationFn: (data: { userId: string }) => {
      return api.post("/profile/settings/rakeback/claim-all", data);
    },
  });
};

export const useGetLeaderboardRakeBack = (
  tier: string,
  page: number = 1,
  pageSize: number = 10,
  userId?: string,
) => {
  return useQuery({
    queryKey: ["get-leaderboard", tier, page, pageSize, userId],
    queryFn: async () => {
      const params = { page, pageSize, userId };
      const response = await api.get(
        `/profile/settings/rakeback/leaderboard/${tier}`,
        { params },
      );
      return response.data;
    },
    enabled: !!tier && !!page && !!pageSize && !!userId,
    staleTime: 5000,
    refetchOnWindowFocus: false,
  });
};

export const useGetUserRank = (userId: string) => {
  return useQuery({
    queryKey: ["get-leaderboard", userId],
    queryFn: async () => {
      const params = { userId };
      const response = await api.get(
        `/profile/settings/rakeback/user-rank/${userId}`,
        { params },
      );
      return response.data;
    },
    enabled: !!userId,
    staleTime: 5000,
    refetchOnWindowFocus: false,
  });
};

export const useGetKYCStatus = () => {
  const config = { withCredentials: true };
  return useQuery({
    queryKey: ["get-all-kyc-status"],
    queryFn: () => backendApi.get("/kyc/kyc-status", config),
  });
};

export const useGenerateAccessToken = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: { levelName: string; ttlInSecs: string }) => {
      return backendApi.post("/kyc/generate-access-token", data, config);
    },
  });
};

export const useUploadImage = () => {
  const config = {
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" },
  };
  return useMutation({
    mutationFn: (image: File) => {
      return backendApi.post(
        "/profile/upload-image",
        {
          image,
        },
        config,
      );
    },
  });
};

export const useSubmitContactForm = () => {
  return useMutation({
    mutationKey: ["sendEmail"],
    mutationFn: async (data) => {
      const res = await backendApi.post("send-support", data);

      const body = await res.data;

      if (res.status != 201) {
        throw new Error(body);
      }

      return body;
    },
  });
};
